import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { listAssignableAgents } from '@/api/leads.api';
import type { UserRef } from '@/types/shared';
import './AgentCombobox.css';

interface AgentComboboxProps {
  id: string;
  /** Selected agent id, or '' for none. */
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** When set, shows a top "none/all" row (e.g. "Leave unassigned" / "All agents") that clears. */
  clearLabel?: string;
  ariaInvalid?: boolean;
}

/**
 * Type-to-filter Sales-Agent picker: shows a text box you type a name into, with a filtered
 * dropdown of matching agents (keyboard + mouse selectable) that resolves to the agent's id.
 * Self-fetches the assignable-agents list, so callers only pass value/onChange.
 */
export function AgentCombobox({
  id,
  value,
  onChange,
  disabled,
  placeholder,
  clearLabel,
  ariaInvalid,
}: AgentComboboxProps) {
  const { t } = useTranslation();
  const [agents, setAgents] = useState<UserRef[]>([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    listAssignableAgents()
      .then((list) => {
        if (!cancelled) setAgents(list);
      })
      .catch(() => {
        if (!cancelled) setAgents([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = agents.find((a) => a.id === value) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? agents.filter((a) => a.fullName.toLowerCase().includes(q)) : agents;
  }, [agents, query]);

  // Flat option list (optional clear row + filtered agents) for keyboard navigation.
  const options: (UserRef | null)[] = clearLabel ? [null, ...filtered] : filtered;

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  function pick(agent: UserRef | null) {
    onChange(agent ? agent.id : '');
    setQuery('');
    setOpen(false);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (open) {
        e.preventDefault();
        pick(options[highlight] ?? null);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div className="fa-combobox" ref={rootRef}>
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        aria-autocomplete="list"
        aria-activedescendant={open && options.length ? `${id}-opt-${highlight}` : undefined}
        aria-invalid={ariaInvalid}
        autoComplete="off"
        className="fa-combobox__input"
        value={open ? query : (selected?.fullName ?? '')}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
      />
      {selected && !open && !disabled && (
        <button
          type="button"
          className="fa-combobox__clear"
          aria-label={t('common.clear')}
          onClick={() => pick(null)}
        >
          &times;
        </button>
      )}
      {open && (
        <ul id={`${id}-listbox`} role="listbox" className="fa-combobox__list">
          {clearLabel && (
            <li
              id={`${id}-opt-0`}
              role="option"
              aria-selected={!value}
              className={`fa-combobox__option${highlight === 0 ? ' is-active' : ''}`}
              onMouseEnter={() => setHighlight(0)}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(null);
              }}
            >
              {clearLabel}
            </li>
          )}
          {filtered.length === 0 ? (
            <li className="fa-combobox__empty">{t('common.noMatches')}</li>
          ) : (
            filtered.map((a, i) => {
              const idx = clearLabel ? i + 1 : i;
              return (
                <li
                  key={a.id}
                  id={`${id}-opt-${idx}`}
                  role="option"
                  aria-selected={a.id === value}
                  className={`fa-combobox__option${highlight === idx ? ' is-active' : ''}`}
                  onMouseEnter={() => setHighlight(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pick(a);
                  }}
                >
                  {a.fullName}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
